import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Bot,
  CalendarDays,
  CheckCircle2,
  LifeBuoy,
  Mail,
  MapPin,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { Seo } from "@/components/seo/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { getSiteUrl } from "@/lib/siteUrl";

const FAQ_IDS = [1, 2, 3, 4, 5] as const;
const FEATURE_IDS = [1, 2, 3, 4] as const;
const FEATURE_ICONS = [MapPin, BarChart3, Zap, Bot] as const;
const HOW_STEP_IDS = [1, 2, 3, 4] as const;
const FIT_ITEM_COUNT = 4;
const ZONE_COUNT = 4;

export default function ApartmentManagersCleaningPartners() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const formAnchorId = "managed-cleaning-contact";

  const siteUrl = getSiteUrl();
  const canonicalPath = "/apartment-manager/partner-pulizie";

  const fitItems = t("am.fit.items", "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const zoneItems = t("am.zones.items", "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Halomora",
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: t("am.seo.description"),
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Consulenza commerciale",
      email: "alessandro@halomora.com",
      availableLanguage: ["Italian", "English"],
    },
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Ricerca e coordinamento partner pulizie per apartment manager",
    name: "Partner pulizie continuativo per appartamenti vacanze",
    provider: {
      "@type": "Organization",
      name: "Halomora",
    },
    areaServed: [
      { "@type": "City", name: "Jesolo", containedInPlace: { "@type": "State", name: "Veneto" } },
      { "@type": "City", name: "Cavallino-Treporti", containedInPlace: { "@type": "State", name: "Veneto" } },
      { "@type": "City", name: "Caorle", containedInPlace: { "@type": "State", name: "Veneto" } },
      { "@type": "City", name: "Bibione", containedInPlace: { "@type": "State", name: "Veneto" } },
      { "@type": "City", name: "Eraclea", containedInPlace: { "@type": "State", name: "Veneto" } },
      { "@type": "State", name: "Veneto" },
    ],
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Apartment manager e property manager",
    },
    keywords: "pulizie Jesolo, aziende di pulizie a Jesolo, pulizie appartamenti Jesolo, pulizie Veneto, property manager Veneto, turnover appartamenti vacanze, pulizie alta stagione",
    description: t("am.seo.description"),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Apartment manager",
        item: `${siteUrl}${canonicalPath}`,
      },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_IDS.map((id) => ({
      "@type": "Question",
      name: t(`am.faq.${id}.q`),
      acceptedAnswer: {
        "@type": "Answer",
        text: t(`am.faq.${id}.a`),
      },
    })),
  };

  const scrollToForm = () => {
    const el = document.getElementById(formAnchorId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const companyName = String(formData.get("company_name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const phone = String(formData.get("phone") || "").trim();
    const contactName = String(formData.get("contact_name") || "").trim();
    const area = String(formData.get("area") || "").trim();
    const apartments = String(formData.get("apartments_count") || "").trim();
    const season = String(formData.get("season_period") || "").trim();
    const needs = String(formData.get("needs") || "").trim();

    const message = [
      contactName ? `${t("am.form.msg.contact")} ${contactName}` : "",
      apartments ? `${t("am.form.msg.apartments")} ${apartments}` : "",
      season ? `${t("am.form.msg.season")} ${season}` : "",
      "",
      t("am.form.msg.needs_label"),
      needs || t("am.form.msg.unspecified"),
    ]
      .filter(Boolean)
      .join("\n");

    try {
      setSubmitting(true);
      const response = await fetch("/api/notifications?action=contact_sales", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: companyName,
          email,
          phone,
          region: area,
          message,
          landing_page: typeof window !== "undefined" ? window.location.href : "",
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          source: "apartment-manager-partner-pulizie",
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || t("am.toast.error_send"));
      }

      setSent(true);
      (e.currentTarget as HTMLFormElement).reset();
      scrollToForm();
      toast({
        title: t("am.toast.sent_title"),
        description: t("am.toast.sent_desc"),
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast({
        title: t("am.toast.failed_title"),
        description: err?.message || t("am.toast.failed_desc"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const batchGrid = [
    { label: t("am.batch.grid.apartments"), desc: t("am.batch.grid.apartments_desc") },
    { label: t("am.batch.grid.windows"), desc: t("am.batch.grid.windows_desc") },
    { label: t("am.batch.grid.ai"), desc: t("am.batch.grid.ai_desc") },
    { label: t("am.batch.grid.alert"), desc: t("am.batch.grid.alert_desc") },
  ];

  return (
    <div className="min-h-screen app-page">
      <Seo
        title={t("am.seo.title")}
        description={t("am.seo.description")}
        canonicalPath={canonicalPath}
        jsonLd={[organizationJsonLd, serviceJsonLd, breadcrumbJsonLd, faqJsonLd]}
      />

      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.28),transparent_36%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.16),transparent_28%),linear-gradient(180deg,rgba(15,23,42,0.94),rgba(15,23,42,0.98))]" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-slate-950/30" />
        <div className="relative container mx-auto px-6 py-16 md:py-20">
          <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-center">
            <div>
              <Badge className="mb-5 border border-white/15 bg-white text-primary hover:bg-white">
                {t("am.hero.badge")}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight tracking-tight">
                {t("am.hero.title")}
              </h1>
              <p className="mt-5 text-lg md:text-xl text-slate-200 max-w-2xl leading-relaxed">
                {t("am.hero.subtitle")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="text-base shadow-xl" onClick={scrollToForm}>
                  {t("am.hero.cta_talk")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/25 bg-white/5 text-white hover:bg-white/12"
                  onClick={() => navigate("/bulk-order")}
                >
                  {t("am.hero.cta_bulk")}
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap gap-3 text-sm text-slate-100">
                <div className="rounded-full border border-white/10 bg-white/8 px-4 py-2 backdrop-blur-sm">
                  {t("am.hero.pill.verified")}
                </div>
                <div className="rounded-full border border-white/10 bg-white/8 px-4 py-2 backdrop-blur-sm">
                  {t("am.hero.pill.batch")}
                </div>
                <div className="rounded-full border border-white/10 bg-white/8 px-4 py-2 backdrop-blur-sm">
                  {t("am.hero.pill.tower")}
                </div>
              </div>
            </div>

            <Card className="border-white/10 bg-white shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl">{t("am.problem.title")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-700">
                <div className="flex gap-3">
                  <CalendarDays className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p>{t("am.problem.p1")}</p>
                </div>
                <div className="flex gap-3">
                  <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p>{t("am.problem.p2")}</p>
                </div>
                <div className="flex gap-3">
                  <LifeBuoy className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p>{t("am.problem.p3")}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t("am.features.title")}</h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto">{t("am.features.subtitle")}</p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {FEATURE_IDS.map((id, idx) => {
              const Icon = FEATURE_ICONS[idx];
              return (
                <Card key={id} className="border-gray-200 shadow-card">
                  <CardContent className="p-6">
                    <Icon className="h-8 w-8 text-primary mb-4" />
                    <h2 className="text-lg font-semibold text-gray-900">{t(`am.feature.${id}.title`)}</h2>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{t(`am.feature.${id}.desc`)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-4 md:py-8">
        <div className="max-w-6xl mx-auto grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <div>
            <Badge variant="outline" className="mb-4">
              {t("am.fit.badge")}
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900">{t("am.fit.title")}</h2>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">{t("am.fit.subtitle")}</p>
            <div className="mt-6 space-y-3">
              {fitItems.slice(0, FIT_ITEM_COUNT).map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-card"
                >
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <Card className="border-gray-200 shadow-card">
            <CardHeader>
              <CardTitle className="text-2xl">{t("am.how.title")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {HOW_STEP_IDS.map((step) => (
                <div key={step} className="rounded-2xl bg-gray-50 p-5 border border-gray-200">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white font-semibold">
                    {step}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">{t(`am.how.${step}.title`)}</h3>
                  <p className="mt-2 text-sm text-gray-600">{t(`am.how.${step}.text`)}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="container mx-auto px-6 py-8 md:py-12">
        <div className="max-w-6xl mx-auto">
          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent-green-500/5 shadow-card">
            <CardContent className="p-6 md:p-8">
              <div className="grid gap-6 lg:grid-cols-[1fr_auto] items-center">
                <div>
                  <Badge className="mb-3">{t("am.cta_find.badge")}</Badge>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t("am.cta_find.title")}</h2>
                  <p className="mt-3 text-gray-700 max-w-3xl">{t("am.cta_find.body")}</p>
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                  <Button onClick={scrollToForm}>{t("am.cta_find.btn_find")}</Button>
                  <Button variant="outline" onClick={() => navigate("/bulk-order")}>
                    {t("am.cta_find.btn_have")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="container mx-auto px-6 py-6 md:py-10">
        <div className="max-w-6xl mx-auto">
          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent-green-500/5 shadow-card">
            <CardContent className="p-6 md:p-8">
              <div className="grid gap-6 lg:grid-cols-[1fr_auto] items-center">
                <div>
                  <Badge className="mb-3">{t("am.batch.badge")}</Badge>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t("am.batch.title")}</h2>
                  <p className="mt-3 text-gray-700 max-w-3xl">{t("am.batch.body")}</p>
                  <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {batchGrid.map((item) => (
                      <div key={item.label} className="rounded-xl border border-primary/10 bg-white/80 p-3 text-center">
                        <div className="text-sm font-semibold text-gray-900">{item.label}</div>
                        <div className="text-xs text-gray-500 mt-1">{item.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                  <Button onClick={scrollToForm}>{t("am.batch.btn_talk")}</Button>
                  <Button variant="outline" onClick={() => navigate("/bulk-order")}>
                    {t("am.batch.btn_try")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="container mx-auto px-6 py-6 md:py-10">
        <div className="max-w-6xl mx-auto grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <Card className="border-gray-200 shadow-card" id={formAnchorId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Mail className="h-5 w-5" />
                {t("am.form.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sent && (
                <div className="mb-5 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                  {t("am.form.success")}
                </div>
              )}
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input name="contact_name" placeholder={t("am.form.contact_name")} required disabled={submitting} />
                <Input name="company_name" placeholder={t("am.form.company_name")} required disabled={submitting} />
                <Input name="email" type="email" placeholder={t("am.form.email")} required disabled={submitting} />
                <Input name="phone" placeholder={t("am.form.phone")} disabled={submitting} />
                <Input name="area" placeholder={t("am.form.area")} disabled={submitting} />
                <Input name="apartments_count" placeholder={t("am.form.apartments")} disabled={submitting} />
                <div className="md:col-span-2">
                  <Input name="season_period" placeholder={t("am.form.season")} disabled={submitting} />
                </div>
                <div className="md:col-span-2">
                  <Textarea name="needs" rows={6} placeholder={t("am.form.needs")} disabled={submitting} />
                </div>
                <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-sm text-gray-500">{t("am.form.hint")}</p>
                  <Button type="submit" disabled={submitting}>
                    {submitting
                      ? t("am.form.submitting")
                      : sent
                        ? t("am.form.sent")
                        : t("am.form.submit")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="border-gray-200 shadow-card">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  {t("am.zones.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-gray-700">
                {zoneItems.slice(0, ZONE_COUNT).map((zona) => (
                  <div key={zona} className="flex items-center gap-2 rounded-xl bg-gray-50 border border-gray-200 p-3">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm">{zona}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-card">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  {t("am.links.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <Button variant="outline" className="justify-start" onClick={() => navigate("/bulk-order")}>
                  {t("am.links.bulk")}
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate("/for-businesses")}>
                  {t("am.links.vendor")}
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate("/")}>
                  {t("am.links.search")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-10 md:py-14">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">{t("am.faq.title")}</h2>
            <p className="mt-3 text-gray-600">{t("am.faq.subtitle")}</p>
          </div>
          <div className="space-y-4">
            {FAQ_IDS.map((id) => (
              <Card key={id} className="border-gray-200 shadow-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t(`am.faq.${id}.q`)}</h3>
                  <p className="mt-2 text-gray-600">{t(`am.faq.${id}.a`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
